import { useCallback, useEffect, useRef } from 'react';
import './index.css';

// Full-screen image viewer for the /media gallery (reusable elsewhere).
// Opens whenever `index` is a valid number into `images` ([{ src, alt }]).
// Closes on Escape / backdrop tap / the ✕; ← → and horizontal swipe move
// between images. Locks body scroll while open and restores focus to the
// element that opened it on close.
function Lightbox({ images = [], index, onClose, onIndex }) {
  const open = typeof index === 'number' && index >= 0 && index < images.length;
  const closeRef = useRef(null);
  const restoreRef = useRef(null);
  const touchX = useRef(null);

  const go = useCallback(
    (delta) => {
      if (images.length < 2) return;
      onIndex((index + delta + images.length) % images.length);
    },
    [images.length, index, onIndex],
  );

  useEffect(() => {
    if (!open) return undefined;
    restoreRef.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      if (restoreRef.current instanceof HTMLElement) restoreRef.current.focus();
    };
  }, [open, onClose, go]);

  if (!open) return null;
  const img = images[index];
  const many = images.length > 1;

  return (
    <div
      className='lightbox'
      role='dialog'
      aria-modal='true'
      aria-label='Photo viewer'
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onTouchStart={(e) => { touchX.current = e.touches[0]?.clientX ?? null; }}
      onTouchEnd={(e) => {
        if (touchX.current == null) return;
        const dx = (e.changedTouches[0]?.clientX ?? touchX.current) - touchX.current;
        touchX.current = null;
        if (many && Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
      }}
    >
      <button ref={closeRef} type='button' className='lightbox-close' onClick={onClose} aria-label='Close'>
        ✕
      </button>

      {many && (
        <button
          type='button'
          className='lightbox-nav lightbox-prev'
          onClick={() => go(-1)}
          aria-label='Previous photo'
        >
          ‹
        </button>
      )}

      <figure className='lightbox-figure' key={index}>
        <img className='lightbox-img' src={img.src} alt={img.alt || ''} />
      </figure>

      {many && (
        <button
          type='button'
          className='lightbox-nav lightbox-next'
          onClick={() => go(1)}
          aria-label='Next photo'
        >
          ›
        </button>
      )}

      {many && (
        <p className='lightbox-count' aria-live='polite'>{index + 1} / {images.length}</p>
      )}
    </div>
  );
}

export default Lightbox;
